import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import {
  currentSourceSelector,
  createCitationSourceTypeSelector,
  createCitationQuerySelector,
} from '@chegg-wt/core/state/createCitation/selectors'
import { defaultSourceType } from '@chegg-wt/core/utils/sourceTypes'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { CROSSREF_RESULT_COUNT } from '@chegg-wt/core/state/optimizely/constants'
import { isInvalidQuery } from '@chegg-wt/core/hooks/useSearchSources'
import { hasWorldcatLink } from '@chegg-wt/core/utils/searchSources'
import { AppState } from '@chegg-wt/core/state'
import { JOURNAL } from '@chegg-wt/core/constants/journalTypes'
import { navigated } from '@chegg-wt/core/state/app/actions'
import {
  sourceQueryEntered,
  journalEngineSelected,
} from '@chegg-wt/core/state/createCitation/actions'

import { currentStyleSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import {
  Region,
  origins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { JournalType, AppRoute, AppRouteOpts } from '@chegg-wt/core/types/core'
import React, { Dispatch } from 'react'
import { connect, useSelector } from 'react-redux'
import SearchBottomContent from '../CitationSearchInput/SearchBottomContent'
import { Box } from '../Layouts/SourceTypeSelection/styled'
import { StyledSourceSearchBar } from '../SourceSearchPageStructure/styled'
import {
  CitationSearchBarDispatchProps,
  CitationSearchBarProps,
  CitationSearchBarStateProps,
} from './types'

const CitationSearchBar: React.FC<CitationSearchBarProps> = ({
  currentStyle: style,
  currentSourceType,
  sourceSearchQuery,
  initialSearchQuery = '',
  onCreateManualCitationClickCTA,
  setSearchQuery,
  changeJournalType,
  navigated,
}) => {
  const selectedSource = useSelector(currentSourceSelector) || defaultSourceType
  const routes = useRoutesContext()
  const { isEnabled: crossrefResultCountEnabled } = useSafeOptimizelyFeature(
    CROSSREF_RESULT_COUNT
  )
  const onChangeJournalType = (type: JournalType) => {
    changeJournalType(type, crossrefResultCountEnabled)
  }
  const handleSearchQuery = (searchQuery: string) => {
    if (!isInvalidQuery(currentSourceType, searchQuery)) onChangeJournalType(JOURNAL)
    if (searchQuery !== sourceSearchQuery) {
      setSearchQuery(searchQuery)
    }
  }

  const isWorldCatSource = hasWorldcatLink(selectedSource.id)
  const handleManualCitationClick = (region: Region) => {
    navigated(routes.toManualCitation({ style, sourceType: selectedSource.value }), {
      replace: true,
    })
    onCreateManualCitationClickCTA(region)
  }
  return (
    <Box>
      <StyledSourceSearchBar
        searchQuery={initialSearchQuery}
        onSearchQueryUpdate={handleSearchQuery}
        isWorldCatSource={isWorldCatSource}
        sourceType={selectedSource}
      />
      <SearchBottomContent
        isWorldCatSource={isWorldCatSource}
        onManualCiteClick={handleManualCitationClick}
        sourceType={selectedSource}
      />
    </Box>
  )
}

const mapStateToProps = (state: AppState) => ({
  currentStyle: currentStyleSelector(state),
  currentSourceType: createCitationSourceTypeSelector(state),
  sourceSearchQuery: createCitationQuerySelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  onCreateManualCitationClickCTA: (region: Region) =>
    dispatch(userClick({ origin: origins.createManualCitationCTA, region })),
  setSearchQuery: (query: string) => dispatch(sourceQueryEntered(query)),
  changeJournalType: (type: JournalType, crossrefResultCountEnabled: boolean) =>
    dispatch(journalEngineSelected(type, crossrefResultCountEnabled)),
  navigated: (route: AppRoute, opts?: AppRouteOpts) => dispatch(navigated(route, opts)),
})

export default connect<CitationSearchBarStateProps, CitationSearchBarDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(CitationSearchBar)
